import { PaymentErrorCodes } from '@app/types/monolith/PaymentErrorCodes';
import { defineMessages, IntlShape } from '@redbubble/next/intl';
import { addDDError } from '../../pages/cart/utils/datadog';
import { ErrorMessage, PayError } from './types';

export class StripePaymentValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StripePaymentValidationError';
  }
}

export const intlErrorMessages = defineMessages({
  unprocessablePayment: {
    defaultMessage:
      'We were unable to process your payment. Please try another payment method.',
  },
  serverError: {
    defaultMessage:
      'We were unable to process your payment. Please contact customer support.',
  },
  retryablePayment: {
    defaultMessage: 'We were unable to process your payment. Please try again.',
  },
  cardDeclined: { defaultMessage: 'The card was declined.' },
  paymentIntentAuthenticationFailure: {
    defaultMessage:
      'We are unable to authenticate your payment method. Please choose a different payment method and try again.',
  },
  expiredCard: { defaultMessage: 'The card has expired.' },
  incorrectCvc: { defaultMessage: 'The card’s security code is incorrect.' },
  processingError: {
    defaultMessage:
      'An error occurred while processing your card. Try again in a little bit.',
  },
  cardNotSupported: { defaultMessage: 'Your card is not supported.' },
  invalidNumber: {
    defaultMessage: 'The card number is not a valid credit card number.',
  },
  somethingWentWrongError: {
    defaultMessage:
      "Oops, something went wrong. Please check the details you've entered and try again.",
  },
});

const errorDict = (code: string, defaultMessage?: string) => {
  return {
    [PaymentErrorCodes.CARD_DECLINED]: intlErrorMessages.cardDeclined,
    [PaymentErrorCodes.CARD_NOT_SUPPORTED]: intlErrorMessages.cardNotSupported,
    [PaymentErrorCodes.CHARGE_FAILED]: intlErrorMessages.unprocessablePayment,
    [PaymentErrorCodes.CHARGE_FAILED_RETRY_ALLOWED]:
      intlErrorMessages.retryablePayment,
    [PaymentErrorCodes.DEFAULT]: defaultMessage,
    [PaymentErrorCodes.EXPIRED_CARD]: intlErrorMessages.expiredCard,
    [PaymentErrorCodes.INCORRECT_CVC]: intlErrorMessages.incorrectCvc,
    [PaymentErrorCodes.INVALID_AMOUNT]: defaultMessage,
    [PaymentErrorCodes.INVALID_COUPON]: defaultMessage,
    [PaymentErrorCodes.INVALID_NUMBER]: intlErrorMessages.invalidNumber,
    [PaymentErrorCodes.PAYMENT_INTENT_AUTHENTICATION_FAILURE]:
      intlErrorMessages.paymentIntentAuthenticationFailure,
    [PaymentErrorCodes.PROCESSING_ERROR]: intlErrorMessages.processingError,
    [PaymentErrorCodes.SERVER_ERROR]: intlErrorMessages.serverError,
    [PaymentErrorCodes.SHIPPING_COUNTRY_BLOCKED]: defaultMessage,
    [PaymentErrorCodes.VALIDATION_ERROR]: defaultMessage,
    [PaymentErrorCodes.USER_ERROR]: intlErrorMessages.somethingWentWrongError,
    [PaymentErrorCodes.FRAUD_DECLINED]: intlErrorMessages.unprocessablePayment,
    [PaymentErrorCodes.LICENSED_WORK_NOT_SALEABLE]: defaultMessage,
    [PaymentErrorCodes.NO_PURCHASABLE_ITEMS]: defaultMessage,
    [PaymentErrorCodes.INVALID_ADDRESS]: defaultMessage,
  }[code];
};

export const getPaymentErrorMessage = ({
  code,
  title,
}: PayError): ErrorMessage => {
  const knownError = errorDict(code, title);

  if (knownError) return knownError;

  addDDError(
    new StripePaymentValidationError(
      `Stripe returned unknown error code "${code}"`
    )
  );

  return intlErrorMessages.unprocessablePayment;
};

export const getErrMsgStr = ({
  errorMessage,
  intl,
}: {
  errorMessage: ErrorMessage;
  intl: IntlShape;
}) => {
  if (typeof errorMessage === 'string') {
    return errorMessage;
  }

  return intl.formatMessage(errorMessage);
};
